import { DateTime } from "luxon";

// Shared by every destination-collecting flow (manual add/edit in
// BuildMyTripWizard.js/TripAI.js, PasteDestinationsFlow.js,
// ScheduleDestinationsFlow.js, ExploreDestinationsFlow.js) — this logic used
// to be independently re-implemented in each one (5+ copies, drifting
// slightly in wording each time). One canonical version here instead.

export const MAX_TRIP_DAYS = 21;

// "3 nights in Siracusa, 1 night in Noto…" comes back from parse_trip_intent
// with each stay ending on its check-out day, which is also the next town's
// first day — so every pair overlapped and Describe your trip's own result
// failed overlapError (2026-10-24). The hand-over day goes to the next town:
// a stay that ends on the day the next one starts ends a day earlier instead
// (never before its own first day). Expects Luxon dateStart/dateEnd.
export const handOverDays = (dests) =>
  dests.map((d, i) => {
    const next = dests[i + 1];
    if (!next || !d.dateEnd || !next.dateStart || !d.dateEnd.hasSame(next.dateStart, "day")) return d;
    const earlier = d.dateEnd.minus({ days: 1 });
    if (earlier.startOf("day") < d.dateStart.startOf("day")) return d;
    return { ...d, dateEnd: earlier };
  });

// Flags the first pair of destinations whose date ranges overlap, so the
// user can resolve it (uncheck/edit one) rather than silently scheduling two
// cities for the same days.
export const overlapError = (dests) => {
  for (let i = 0; i < dests.length; i++) {
    for (let j = i + 1; j < dests.length; j++) {
      if (dests[i].dateStart < dests[j].dateEnd && dests[j].dateStart < dests[i].dateEnd) {
        return `Destination ${i + 1} (${dests[i].city}) and destination ${j + 1} (${dests[j].city}) have overlapping dates.`;
      }
    }
  }
  return null;
};

// Total distinct calendar days spanned by every REAL destination's date
// range (union, not sum — overlapping/adjacent destinations don't
// double-count). A noPlace placeholder day ("Sea Day"/"Travel Day" — city
// exactly "", see DestinationsScreen.js) doesn't count against the trip
// length cap: it adds nothing to the place-search/schedule-build workload
// the cap exists to bound. `d.city !== ""` (not just truthy) deliberately
// still counts a caller that passes a plain date-range object with no city
// field at all (e.g. ExploreDestinationsFlow.js's overall-span check).
export const totalDaySpan = (dests) => {
  const daySet = new Set();
  dests.filter((d) => d.city !== "").forEach((d) => {
    let cursor = d.dateStart.startOf("day");
    const endDay = d.dateEnd.startOf("day");
    while (cursor <= endDay) {
      daySet.add(cursor.toISODate());
      cursor = cursor.plus({ days: 1 });
    }
  });
  return daySet.size;
};

export const tripLengthError = (dests) => {
  const days = totalDaySpan(dests);
  if (days > MAX_TRIP_DAYS) return `Total trip is ${days} days — maximum is ${MAX_TRIP_DAYS}.`;
  return null;
};

// The persisted Guide.destinations list stores only these fields (plus id) —
// strip whatever transient review-only fields a source flow (Explore, derived
// candidates, DestinationsScreen.js's own noPlace edit state) may have
// attached before either loading into working state or saving back out.
export const cleanDestination = ({ id, city, country, dateStart, dateEnd, hasTransportTimes, noSearch, waypointLabel, built, builtAs }) => ({
  id,
  city,
  country,
  dateStart,
  dateEnd,
  ...(hasTransportTimes ? { hasTransportTimes } : {}),
  // noSearch: a waypoint destination — real, geocoded place kept on the
  // map/schedule but excluded from Build My Trip's attraction search (e.g.
  // a fuel stop or overnight stay not worth researching). Distinct from
  // noPlace (city === ""), which has no place at all. Only meaningful when
  // the destination has a real city, so it's dropped along with noPlace's
  // stripped edit-only state whenever false/absent.
  ...(noSearch ? { noSearch } : {}),
  // waypointLabel: optional free-text description for a noSearch waypoint
  // (e.g. "Hotel", "Fuel Stop") — the place/event otherwise falls back to
  // the city name alone, which doesn't say WHAT's there. Only meaningful
  // alongside noSearch, so dropped the same way.
  ...(noSearch && waypointLabel ? { waypointLabel } : {}),
  // built: set once BuildMyTripWizard.js actually finishes searching/
  // scheduling this destination (see markDestinationBuilt there). Lets any
  // flow that appends new destinations onto an already-built guide (Import
  // Itinerary from Image/Text, the normal Add Destination screen) re-open
  // Build My Trip without it silently re-searching and re-scheduling
  // destinations that were already done in an earlier run — a real report:
  // importing more destinations onto a guide caused the whole trip,
  // including already-built stops, to be reprocessed. Found 2026-09-26.
  ...(built ? { built } : {}),
  // builtAs: what was built before the city or dates were changed (see
  // editDestination), so Build My Trip can replace those events.
  ...(builtAs ? { builtAs } : {}),
});

// A change to a destination's city, country or dates (2026-10-23). A built
// one keeps what was built in builtAs ({ city, country, dateStart, dateEnd })
// instead of just losing its built mark, so Build My Trip replaces those
// events (e.g. Venice → Verona on the same days, or Florence moved to other
// days) rather than leaving them next to the new ones. Changing it back to
// exactly what was built makes it built again.
export const editDestination = (dest, changes) => {
  const next = { ...dest, ...changes };
  const was = dest.builtAs || (dest.built ? { city: dest.city, country: dest.country, dateStart: dest.dateStart, dateEnd: dest.dateEnd } : null);
  if (!was) return { ...next, built: undefined };
  const same = was.city === next.city && was.country === next.country &&
    next.dateStart && next.dateEnd && +asLuxon(was.dateStart) === +asLuxon(next.dateStart) && +asLuxon(was.dateEnd) === +asLuxon(next.dateEnd);
  return same ? { ...next, built: true, builtAs: undefined } : { ...next, built: undefined, builtAs: was };
};

// "Already in this trip" (2026-10-23): a built destination counts only while
// the schedule being built into still has it. The built mark alone went
// stale when that destination's events were later deleted, or when building
// into a different schedule (e.g. a new "Plan B"), so Build My Trip skipped
// a destination that wasn't there. An event counts when it falls within the
// destination's saved dates and one of its places is in that city or in the
// destination's group (Build My Trip names it after the city, "-2" for a
// repeat). The built mark is still required, so imported hotels/flights in
// those days don't make a never-built destination look done.
export const inTripEvents = (dest, guide, scheduleName) => {
  if (!dest?.built) return [];
  const saved = (guide?.destinations || []).find((d) => d.id === dest.id) || dest;
  return eventsFor({ city: dest.city, dateStart: saved.dateStart, dateEnd: saved.dateEnd }, guide, scheduleName);
};

export const inTripSchedule = (dest, guide, scheduleName) => inTripEvents(dest, guide, scheduleName).length > 0;

// The events a changed destination replaces: what was built before its city
// or dates changed (builtAs, see editDestination).
export const replacedEvents = (dest, guide, scheduleName) => (dest?.builtAs ? eventsFor(dest.builtAs, guide, scheduleName) : []);

// Before a destination is built (2026-10-23): takes out of the schedule the
// events it replaces — its own when it's redone (inTripEvents), or what it
// was before a change (replacedEvents) — so the new ones replace them
// instead of doubling up. Other destinations, bookings and travel stay.
// Build My Trip's "Lunch" has no place, so a placeless "Lunch" goes too on a
// day left with no other placed event. Returns the number removed.
export const removeFromSchedule = (dest, guide, scheduleName) => {
  const gone = [...new Set([...inTripEvents(dest, guide, scheduleName), ...replacedEvents(dest, guide, scheduleName)])];
  if (!gone.length) return 0;
  const days = new Set(gone.map((e) => asLuxon(e.Start).toISODate()));
  gone.forEach((e) => guide.schedule.deleteEvent(e));
  const left = guide.schedule.getEventsFromSchedule(scheduleName);
  const placedDays = new Set(left.filter((e) => String(e.Places || "").trim()).map((e) => asLuxon(e.Start).toISODate()));
  const lunches = left.filter((e) => e.Subject === "Lunch" && !String(e.Places || "").trim() && days.has(asLuxon(e.Start).toISODate()) && !placedDays.has(asLuxon(e.Start).toISODate()));
  lunches.forEach((e) => guide.schedule.deleteEvent(e));
  return gone.length + lunches.length;
};

// The schedule's events at a city's places during its days.
const eventsFor = ({ city: cityName, dateStart, dateEnd }, guide, scheduleName) => {
  if (!cityName || !dateStart || !dateEnd || !scheduleName || !guide?.schedule) return [];
  const first = asLuxon(dateStart).toISODate();
  const last = asLuxon(dateEnd).toISODate();
  const city = cityName.trim().toLowerCase();
  const base = cityName.trim().substring(0, 20).toLowerCase();
  const groupIds = new Set(
    (guide.groups || [])
      .filter((g) => { const n = String(g.name || "").toLowerCase(); return n === base || n.startsWith(base + "-"); })
      .map((g) => g.id)
  );
  const byId = new Map((guide.places?.placesList || []).map((p) => [p.ID, p]));
  return guide.schedule.getEventsFromSchedule(scheduleName).filter((e) => {
    if (e.IsTravel || !e.Start) return false;
    const day = asLuxon(e.Start).toISODate();
    if (day < first || day > last) return false;
    return String(e.Places || "").split(",").some((id) => {
      const p = byId.get(id.trim());
      return !!p && (String(p.City || "").trim().toLowerCase() === city || (p.Group || []).some((g) => groupIds.has(g)));
    });
  });
};

const asLuxon = (value) => (DateTime.isDateTime(value) ? value : DateTime.fromISO(String(value), { zone: "utc" }));
